// src/services/azureCostEstimatePaymentsService.js
// Azure Table Storage Service fuer Zahlungen der Kostenkontrolle (KOST-010).
// Table costEstimatePayments:
//   PartitionKey = estimateId
//   RowKey       = `${code}__${uuid}`
//   Properties   = Phase, Code, Bezeichnung, Betrag, Datum, IstAkonto
//                  + UnternehmerId, UnternehmerName, ZaNr, RechnungNr, ZaDaten
//                    (US-KOST-022, Zahlungsanweisung): ZaNr = laufende Nummer
//                    der Zahlungsanweisung, ZaDaten = JSON-Snapshot aller
//                    Angaben des erzeugten PDFs (erlaubt erneuten Download).
//                  + VertragId (US-KOST-023): RowKey des Vertrags der Position,
//                    zu dem die Zahlung gehoert (Vertragsgruppe, siehe
//                    services/costs/contractGroups.js). Leer bei Altdaten.
//
// Zahlungen koennen auch auf eine Aggregat-Position (Ueberposition) gebucht
// werden (KOST-014); die Code-Zuordnung erfolgt ueber das Code-Feld.

import { ensureTableExists } from "./azureTableUtils.js";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "costEstimatePayments",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

function toBool(val) {
  return val === true || val === "true";
}

async function queryEntities(filter) {
  let all = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}&${filter}`;
    if (nextPartitionKey) url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
    if (nextRowKey) url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (!response.ok) {
      if (response.status === 404) break;
      throw new Error(`Azure query error: ${response.status}`);
    }
    const data = await response.json();
    all = all.concat(data.value || []);
    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey || nextRowKey);

  return all;
}

function mapEntity(e) {
  return {
    id: e.RowKey,
    code: e.Code || "",
    phase: e.Phase || "",
    bezeichnung: e.Bezeichnung || "",
    betrag: parseFloat(e.Betrag) || 0,
    datum: e.Datum || "",
    istAkonto: toBool(e.IstAkonto),
    unternehmerId: e.UnternehmerId || "",
    unternehmerName: e.UnternehmerName || "",
    zaNr: e.ZaNr != null && e.ZaNr !== "" ? String(e.ZaNr) : "",
    rechnungNr: e.RechnungNr || "",
    zaDaten: parseZaDaten(e.ZaDaten),
    vertragId: e.VertragId || "",
  };
}

function parseZaDaten(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// Gemeinsamer Entity-Aufbau fuer Anlegen und Aktualisieren. PUT ersetzt die
// Entity vollstaendig — jedes Feld muss hier stehen, sonst geht es beim
// naechsten Update verloren (siehe lessons-learned 2026-08-24).
function buildEntity(estimateId, rowKey, data) {
  return {
    PartitionKey: estimateId,
    RowKey: rowKey,
    Phase: data.phase || "",
    Code: data.code || "",
    Bezeichnung: data.bezeichnung || "",
    Betrag: parseFloat(data.betrag) || 0,
    Datum: data.datum || "",
    IstAkonto: !!data.istAkonto,
    UnternehmerId: data.unternehmerId || "",
    UnternehmerName: data.unternehmerName || "",
    ZaNr: data.zaNr != null ? String(data.zaNr) : "",
    RechnungNr: data.rechnungNr || "",
    ZaDaten: data.zaDaten ? JSON.stringify(data.zaDaten) : "",
    VertragId: data.vertragId || "",
    UpdatedAt: new Date().toISOString(),
  };
}

async function upsert(estimateId, rowKey, entity) {
  const pk = encodeURIComponent(estimateId);
  const rk = encodeURIComponent(rowKey);
  const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure upsert error: ${response.status} - ${errorText}`);
  }
}

// Alle Zahlungen eines Dossiers fuer eine Phase laden.
export const getPaymentsByEstimate = async (estimateId, phase) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const filter = phase
      ? `$filter=PartitionKey eq '${estimateId}' and Phase eq '${phase}'`
      : `$filter=PartitionKey eq '${estimateId}'`;
    const entities = await queryEntities(filter);
    return entities.map(mapEntity);
  } catch (error) {
    console.error("Load payments error:", error);
    return [];
  }
};

// Neue Zahlung anlegen.
export const savePayment = async (estimateId, data) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const rowKey = `${data.code}__${crypto.randomUUID()}`;
    const entity = buildEntity(estimateId, rowKey, data);
    await upsert(estimateId, rowKey, entity);
    return mapEntity(entity);
  } catch (error) {
    console.error("Save payment error:", error);
    throw error;
  }
};

// Bestehende Zahlung aktualisieren.
export const updatePayment = async (estimateId, paymentId, data) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const entity = buildEntity(estimateId, paymentId, data);
    await upsert(estimateId, paymentId, entity);
    return mapEntity(entity);
  } catch (error) {
    console.error("Update payment error:", error);
    throw error;
  }
};

// Phase umbenennen (US-KOST-019): alle Zahlungen der alten Phase auf den neuen
// Phasennamen umschreiben. Der RowKey (`${code}__${uuid}`) enthaelt die Phase
// nicht — es genuegt, die Phase-Property zu ersetzen (kein copy-then-delete noetig).
export const renamePaymentsPhase = async (estimateId, oldPhase, newPhase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(oldPhase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    const clone = { ...e, Phase: newPhase, UpdatedAt: new Date().toISOString() };
    delete clone["odata.etag"];
    delete clone.Timestamp;
    await upsert(estimateId, e.RowKey, clone);
  }
  return entities.length;
};

// Zahlung loeschen.
export const deletePayment = async (estimateId, paymentId) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(paymentId);
    const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, { method: "DELETE", headers: { "If-Match": "*" } });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete error: ${response.status}`);
    }
  } catch (error) {
    console.error("Delete payment error:", error);
    throw error;
  }
};

// Phase kopieren (US-KOST-019): alle Zahlungen der Quell-Phase in die Ziel-Phase
// duplizieren ("Kostenkontrolle als Arbeitskopie"). Jede Kopie erhaelt zwingend
// einen NEUEN RowKey (`${code}__${uuid}`) — der RowKey ist eine reine ID; mit dem
// alten wuerde die Quelle ueberschrieben statt dupliziert.
export const copyPaymentsPhase = async (estimateId, sourcePhase, targetPhase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(sourcePhase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    const rowKey = `${e.Code || ""}__${crypto.randomUUID()}`;
    const clone = { ...e, PartitionKey: estimateId, RowKey: rowKey, Phase: targetPhase, UpdatedAt: new Date().toISOString() };
    delete clone["odata.etag"];
    delete clone.Timestamp;
    await upsert(estimateId, rowKey, clone);
  }
  return entities.length;
};

// Phase loeschen (US-KOST-019): alle Zahlungen der Phase endgueltig entfernen.
export const deletePaymentsPhase = async (estimateId, phase) => {
  await ensureTableExists(AZURE_CONFIG.tableName);
  const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${String(phase).replace(/'/g, "''")}'`;
  const entities = await queryEntities(filter);
  for (const e of entities) {
    await deletePayment(estimateId, e.RowKey);
  }
  return entities.length;
};
